import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import RolesRequest from "../../requests/vendor/RolesRequest";
import {
  setCreating,
  setUpdating,
  setDeleting,
  setError,
  setCreateError,
  setUpdateError,
  setDeleteError,
  setTeamMembers,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  updateTeamMemberStatus,
  setCanCreateMembers,
  clearErrors,
} from "../../redux/TeamSlice";
import { useCustomRoles } from "./useCustomRoles";
import { useEffect } from "react";
import { 
  canViewTeamMembers, 
  canCreateTeamMembers, 
  canEditTeamMembers, 
  canDeleteTeamMembers 
} from "../../utils/rolesPermissions";

//Todo => Query keys
export const TEAM_QUERY_KEYS = {
  teamMembers: "teamMembers",
  teamMember: (id) => ["teamMember", id],
};

//Todo => Custom hook for fetching all team members
export const useTeamMembers = () => {
  const dispatch = useDispatch();
  const teamMembers = useSelector((state) => state.team.teamMembers);
  const isLoading = useSelector((state) => state.team.isLoading);
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);

  const canViewMembers = canViewTeamMembers(userPermissions, isMainVendor);

  const query = useQuery(
    TEAM_QUERY_KEYS.teamMembers,
    RolesRequest.getAllTeamMembers,
    {
      enabled: canViewMembers,
      onSuccess: (data) => {
        if (data.success) {
          dispatch(setTeamMembers(data.data));
        } else {
          dispatch(setError(data.message));
          toast.error(data.message);
        }
      },
      onError: (error) => {
        const errorMessage = error.message || "Failed to fetch team members";
        dispatch(setError(errorMessage));
        toast.error(errorMessage);
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    teamMembers,
    isLoading: query.isLoading || isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

//Todo => Custom hook for creating team members with role validation
export const useCreateTeamMember = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isCreating = useSelector((state) => state.team.isCreating);
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);
  const { customRoles } = useCustomRoles();
  
  // Check if user can create team members
  const canCreateMembers = canCreateTeamMembers(userPermissions, isMainVendor);

  const mutation = useMutation(RolesRequest.createTeamMember, {
    onMutate: (userData) => {
      if (customRoles.length === 0) {
        throw new Error(
          "Cannot create team member: No roles available. Please create at least one role first."
        );
      }

      // Validate role exists
      const roleExists = customRoles.some(
        (role) => role.role_slug === userData.roleSlug
      );
      if (!roleExists) {
        throw new Error(
          `Cannot create team member: Role "${userData.role}" does not exist.`
        );
      }

      dispatch(setCreating(true));
      dispatch(clearErrors());
    },
    onSuccess: (data) => {
      if (data.success) {
        dispatch(addTeamMember(data.data));
        toast.success(data.message || "Team member created successfully");

        //Todo => Invalidate and refetch team members
        queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);
      }
    },
    onError: (error) => {
      const errorMessage = error.message || "Failed to create team member";
      dispatch(setCreateError(errorMessage));
      toast.error(errorMessage);
    },
    onSettled: () => {
      dispatch(setCreating(false));
    },
  });

  const createTeamMemberWithCallbacks = async (userData, callbacks = {}) => {
    try {
      dispatch(setCreating(true));
      dispatch(clearErrors());

      if (customRoles.length === 0) {
        throw new Error(
          "Cannot create team member: No roles available. Please create at least one role first."
        );
      }
      const roleExists = customRoles.some(
        (role) => role.role_slug === userData.roleSlug
      );
      if (!roleExists) {
        throw new Error(
          `Cannot create team member: Role "${userData.role}" does not exist.`
        );
      }
      const data = await RolesRequest.createTeamMember(userData);

      if (data.success) {
        dispatch(addTeamMember(data.data));
        queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);

        if (callbacks.onSuccess) {
          callbacks.onSuccess(data);
        } else {
          toast.success(data.message || "Team member created successfully");
        }
      } else {
        dispatch(setCreateError(data.message));

        if (callbacks.onError) {
          callbacks.onError(
            new Error(data.message || "Failed to create team member")
          );
        } else {
          toast.error(data.message || "Failed to create team member");
        }
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to create team member";
      dispatch(setCreateError(errorMessage));

      if (callbacks.onError) {
        callbacks.onError(error);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      dispatch(setCreating(false));
    }
  };

  return {
    createTeamMember: createTeamMemberWithCallbacks,
    isCreating: isCreating,
    canCreateMembers: canCreateMembers && customRoles.length > 0,
    error: mutation.error,
    requiresRoles: customRoles.length === 0,
  };
};

//Todo => Custom hook for updating team members with role validation
export const useUpdateTeamMember = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isUpdating = useSelector((state) => state.team.isUpdating);
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);
  const { customRoles } = useCustomRoles();
  
  // Check if user can edit team members
  const canEditMembers = canEditTeamMembers(userPermissions, isMainVendor);

  const mutation = useMutation(
    (userData) => RolesRequest.updateTeamMember(userData),
    {
      onMutate: (userData) => {
        if (userData.role) {
          const roleExists = customRoles.some(
            (role) => role.name === userData.role
          );
          if (!roleExists) {
            throw new Error(
              `Cannot update team member: Role "${userData.role}" does not exist.`
            );
          }
        }

        dispatch(setUpdating(true));
        dispatch(clearErrors());
      },
      onSuccess: (data, variables) => {
        if (data.success) {
          dispatch(updateTeamMember(data.data));
          toast.success(data.message || "Team member updated successfully");

          queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);
        } else {
          dispatch(setUpdateError(data.message));
          toast.error(data.message);
        }
      },
      onError: (error) => {
        const errorMessage = error.message || "Failed to update team member";
        dispatch(setUpdateError(errorMessage));
        toast.error(errorMessage);
      },
      onSettled: () => {
        dispatch(setUpdating(false));
      },
    }
  );

  const updateTeamMemberWithCallbacks = async (userData, callbacks = {}) => {
    try {
      dispatch(setUpdating(true));
      dispatch(clearErrors());
      if (userData.role && !userData.role_slug) {
        const roleExists = customRoles.some(
          (role) => role.name === userData.role
        );
        if (!roleExists) {
          throw new Error(
            `Cannot update team member: Role "${userData.role}" does not exist.`
          );
        }
      }
      const selectedRole = userData.role
        ? customRoles.find((role) => role.name === userData.role)
        : null;
      const completeUserData = {
        ...userData,
        selectedRoleData: selectedRole,
      };
      const data = await RolesRequest.updateTeamMember(completeUserData);
      if (data.success) {
        dispatch(updateTeamMember(data.data));
        queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);

        if (callbacks.onSuccess) {
          callbacks.onSuccess(data);
        }
        return data;
      } else {
        //Todo => Don't throw error for validation errors, return the data so component can handle it
        if (data.statusCode === "422" && data.errors) {
          return data;
        }
        
        dispatch(setUpdateError(data.message));

        if (callbacks.onError) {
          callbacks.onError(
            new Error(data.message || "Failed to update team member")
          );
        }
        throw new Error(data.message || "Failed to update team member");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to update team member";
      dispatch(setUpdateError(errorMessage));

      if (callbacks.onError) {
        callbacks.onError(error);
      }
      throw error;
    } finally {
      dispatch(setUpdating(false));
    }
  };

  return {
    updateTeamMember: updateTeamMemberWithCallbacks,
    isUpdating,
    canEditMembers,
    error: mutation.error,
  };
};

//Todo => Custom hook for deleting team members
export const useDeleteTeamMember = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isDeleting = useSelector((state) => state.team.isDeleting);
  const { isMainVendor, userPermissions } = useSelector((state) => state.user);
  
  // Check if user can delete team members
  const canDeleteMembers = canDeleteTeamMembers(userPermissions, isMainVendor);

  const mutation = useMutation(RolesRequest.deleteTeamMember, {
    onMutate: () => {
      dispatch(setDeleting(true));
      dispatch(clearErrors());
    },
    onSuccess: (data, userId) => {
      if (data.success) {
        dispatch(removeTeamMember(userId));
        toast.success(data.message || "Team member deleted successfully");

        //Todo => Invalidate and refetch team members
        queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);
        queryClient.removeQueries(TEAM_QUERY_KEYS.teamMember(userId));
      } else {
        dispatch(setDeleteError(data.message));
        toast.error(data.message);
      }
    },
    onError: (error) => {
      const errorMessage = error.message || "Failed to delete team member";
      dispatch(setDeleteError(errorMessage));
      toast.error(errorMessage);
    },
    onSettled: () => {
      dispatch(setDeleting(false));
    },
  });

  return {
    deleteTeamMember: mutation.mutate,
    isDeleting: mutation.isLoading || isDeleting,
    canDeleteMembers,
    error: mutation.error,
  };
};

//Todo => Custom hook for updating team member status
export const useUpdateTeamMemberStatus = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isUpdating = useSelector((state) => state.team.isUpdating);

  const mutation = useMutation(
    ({ userId, status }) => RolesRequest.updateTeamMemberStatus(userId, status),
    {
      onMutate: () => {
        dispatch(setUpdating(true));
        dispatch(clearErrors());
      },
      onSuccess: (data, variables) => {
        if (data.success) {
          dispatch(
            updateTeamMemberStatus({
              id: variables.userId,
              status: variables.status,
            })
          );
          toast.success(data.message);

          //Todo => Invalidate and refetch team members
          queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);
          queryClient.invalidateQueries(
            TEAM_QUERY_KEYS.teamMember(variables.userId)
          );
        } else {
          dispatch(setUpdateError(data.message));
          toast.error(data.message);
        }
      },
      onError: (error) => {
        const errorMessage =
          error.message || "Failed to update team member status";
        dispatch(setUpdateError(errorMessage));
        toast.error(errorMessage);
      },
      onSettled: () => {
        dispatch(setUpdating(false));
      },
    }
  );

  return {
    updateTeamMemberStatus: mutation.mutate,
    isUpdating: mutation.isLoading || isUpdating,
    error: mutation.error,
  };
};

//Todo => Custom hook for fetching a specific team member
export const useTeamMember = (userId) => {
  const query = useQuery(
    TEAM_QUERY_KEYS.teamMember(userId),
    () => RolesRequest.getTeamMemberById(userId),
    {
      enabled: !!userId,
      onError: (error) => {
        const errorMessage = error.message || "Failed to fetch team member";
        toast.error(errorMessage);
      },
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
    }
  );

  return {
    teamMember: query.data?.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

//Todo => Enhanced utility hook for team management operations with role integration
export const useTeamManagement = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  //Todo => Get all team-related state
  const teamState = useSelector((state) => state.team);
  const { isMainVendor } = useSelector((state) => state.user);

  //Todo => Get custom roles integration
  const { customRoles, hasRoles, isLoading: rolesLoading } = useCustomRoles();

  //Todo => Get all custom hooks
  const {
    teamMembers,
    isLoading: isFetchingMembers,
    refetch: refetchMembers,
  } = useTeamMembers();

  const { createTeamMember, isCreating, canCreateMembers, requiresRoles } =
    useCreateTeamMember();

  const { updateTeamMember, isUpdating, canEditMembers } = useUpdateTeamMember();
  const { deleteTeamMember, isDeleting, canDeleteMembers } = useDeleteTeamMember();
  const { updateTeamMemberStatus } = useUpdateTeamMemberStatus();

  //Todo => Update canCreateMembers based on available roles
  useEffect(() => {
    dispatch(setCanCreateMembers(isMainVendor && hasRoles));
  }, [dispatch, isMainVendor, hasRoles]);

  //Todo => Utility functions
  const refreshTeamData = () => {
    queryClient.invalidateQueries(TEAM_QUERY_KEYS.teamMembers);
    queryClient.invalidateQueries("customRoles");
    refetchMembers();
  };

  const clearAllErrors = () => {
    dispatch(clearErrors());
  };

  const isAnyLoading =
    isFetchingMembers || rolesLoading || isCreating || isUpdating || isDeleting;

  //Todo => Role-related utilities
  const getRolePermissions = (roleName) => {
    const role = customRoles.find((r) => r.name === roleName);
    return role ? role.permissions : {};
  };

  const getTeamMembersByRole = (roleName) => {
    return teamMembers.filter((member) => member.role === roleName);
  };

  const canDeleteRole = (roleName) => {
    return getTeamMembersByRole(roleName).length === 0;
  };

  //Todo => Phone number formatting utility
  const formatPhoneForApi = (phone) => {
    if (!phone) return "";

    const cleanPhone = phone.trim().replace(/\s/g, "");

    if (cleanPhone.startsWith("0")) {
      return "233" + cleanPhone.substring(1);
    }

    if (cleanPhone.startsWith("233")) {
      return cleanPhone;
    }

    return cleanPhone;
  };

  //Todo => Validation helpers
  const validateTeamMemberData = (memberData) => {
    const errors = {};

    if (!memberData.fullName?.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!memberData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberData.email)) {
      errors.email = "Invalid email format";
    }

    if (!memberData.phone?.trim()) {
      errors.phone = "Phone number is required";
    } else {
      const phone = memberData.phone.trim().replace(/\s/g, "");

      if (phone.startsWith("0")) {
        if (phone.length !== 10) {
          errors.phone =
            "Phone number starting with 0 must be exactly 10 digits";
        } else if (!/^0\d{9}$/.test(phone)) {
          errors.phone = "Invalid phone number format";
        }
      }
      else if (phone.startsWith("233")) {
        if (phone.length !== 12) {
          errors.phone =
            "Phone number starting with 233 must be exactly 12 digits";
        } else if (!/^233\d{9}$/.test(phone)) {
          errors.phone = "Invalid phone number format";
        }
      }
      else {
        errors.phone =
          "Phone number must start with 0 (10 digits) or 233 (12 digits)";
      }
    }

    if (!memberData.role?.trim()) {
      errors.role = "Role is required";
    } else if (!memberData.roleSlug?.trim()) {
      errors.role = "Invalid role selection";
    } else if (
      !customRoles.some((role) => role.role_slug === memberData.roleSlug)
    ) {
      errors.role = "Selected role does not exist";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  return {
    //Todo => Data
    teamMembers,
    customRoles,
    teamState,

    //Todo => Loading states
    isLoading: isAnyLoading,
    isFetchingMembers,
    rolesLoading,
    isCreating,
    isUpdating,
    isDeleting,

    //Todo => Role dependencies
    hasRoles,
    requiresRoles,
    canCreateMembers: canCreateMembers && !requiresRoles,
    canEditMembers,
    canDeleteMembers,

    //Todo => Actions
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    updateTeamMemberStatus,

    //Todo => Role utilities
    getRolePermissions,
    getTeamMembersByRole,
    canDeleteRole,

    //Todo => Validation
    validateTeamMemberData,

    //Todo => Utilities
    refreshTeamData,
    clearAllErrors,
    refetchMembers,
    formatPhoneForApi,

    //Todo => Access control
    isMainVendor,
  };
};
